/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { css } from '@emotion/react';
import { Stack } from '../../../design-system/layout/Stack';
import { Drawer } from '../../../design-system/molecules/Drawer/Drawer';
import { DataBrowserToolbar } from '../../organisms/DataBrowserToolbar/DataBrowserToolbar';
import { DataGrid } from '../../organisms/DataGrid/DataGrid';
import { RowDetailPanel } from '../../organisms/RowDetailPanel/RowDetailPanel';
import { Spinner } from '../../../design-system/atoms/Spinner/Spinner';
import { theme } from '../../../design-system/theme/theme';
import { linkifyValue } from '../../utils/linkify';
import { stripTimePrecision } from '../../utils/formatValue';
import { getHiddenColumns, getRoundColumns, getDetailHiddenColumns, getRelatedColumns, getColumnComments } from '../../config/dataCatalog';
import { Button } from '../../../design-system/atoms/Button/Button';
import { DocViewer } from '../../organisms/DocViewer/DocViewer';
import { isDocRow, extractDocRow } from '../../utils/docRow';

export interface DataBrowserScreenProps {
  moduleOptions: string[];
  module: string;
  onChangeModule: (m: string) => void;
  tableOptions: { label: string; value: string }[];
  table: string;
  onChangeTable: (t: string) => void;
  search: string;
  onSearchChange: (s: string) => void;
  columns: string[];
  rows: (string | number)[][];
  total: number;
  page: number;
  onPrev: () => void;
  onNext: () => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedIndex: number;
  onSelectRow: (i: number) => void;
  /** 행 상세 드로어 열림 여부. */
  detailOpen: boolean;
  /** 드로어 닫기. */
  onCloseDetail: () => void;
  fk?: { table: string; label: string };
  related: { title: string; columns: string[]; rows: (string | number)[][] } | null;
  relatedLoading: boolean;
  relatedError: string | null;
  onOpenFk: () => void;
}

const noteStyle = css`
  margin: 0;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};
`;

const gridMsgStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: ${theme.space.sm}px;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};
`;

/* 드로어 상단 닫기 줄. RowDetailPanel 위에 얹는다. */
const drawerHeaderStyle = css`
  display: flex;
  justify-content: flex-end;
  padding: ${theme.space.sm}px ${theme.space.md}px 0;
`;

const drawerBodyStyle = css`
  padding: 0 ${theme.space.md}px ${theme.space.md}px;
`;

const LIMIT = 50;

/** 데이터 브라우저 = 범용 테이블 뷰어. 컬럼은 응답 스키마에서 그대로 렌더링. */
export function DataBrowserScreen(props: DataBrowserScreenProps) {
  const {
    moduleOptions, module, onChangeModule,
    tableOptions, table, onChangeTable,
    search, onSearchChange,
    columns, rows, total, page, onPrev, onNext,
    loading, error, onRetry,
    selectedIndex, onSelectRow,
    detailOpen, onCloseDetail,
    fk, related, relatedLoading, relatedError, onOpenFk,
  } = props;

  const stringRows: string[][] = rows.map((row) => row.map((c) => (c === null ? '—' : String(c))));
  const offset = page * LIMIT;
  const rangeLabel = total === 0 ? '0' : `${offset + 1}–${offset + rows.length}`;
  const selectedRow = stringRows[selectedIndex];

  // 문서 뷰어(docparser 산출물) — 열림 상태는 화면 로컬 UI 상태.
  const [docOpen, setDocOpen] = useState(false);
  // 원본 행을 쓴다 (stringRows는 null을 '—'로 바꿔 경로 파싱을 망친다).
  const rawRow = rows[selectedIndex];
  const docRow = isDocRow(columns) && rawRow ? extractDocRow(columns, rawRow) : null;

  // 그리드에서만 감출 컬럼 적용 (행 상세 패널에는 전체 컬럼 노출).
  const hidden = getHiddenColumns(module, table);
  const visibleIdx = columns
    .map((c, i) => (hidden.includes(c) ? -1 : i))
    .filter((i) => i >= 0);

  const gridColumns = visibleIdx.map((i) => columns[i]);

  // round 지정 컬럼은 원본 숫자값을 소수점 2자리로 반올림해 표시 (행 상세에는 원본 노출).
  const round = getRoundColumns(module, table);
  const formatCell = (colIndex: number, raw: string | number | null): string => {
    if (raw === null || raw === undefined) return '—';
    if (round.includes(columns[colIndex])) {
      const n = Number(raw);
      if (Number.isFinite(n)) return n.toFixed(2);
    }
    return stripTimePrecision(String(raw));
  };
  const gridRows = rows.map((row) => visibleIdx.map((i) => formatCell(i, row[i])));

  // 행 상세 패널에서 제외할 컬럼 (항상 비어있는 컬럼 등).
  const detailHidden = getDetailHiddenColumns(module, table);
  const detailIdx = columns
    .map((c, i) => (detailHidden.includes(c) ? -1 : i))
    .filter((i) => i >= 0);

  // 연관 행(댓글) 뷰는 화이트리스트 컬럼만(지정 순서) 표시.
  const relatedWhitelist = getRelatedColumns(module, table);
  const relatedView =
    related && relatedWhitelist.length > 0
      ? (() => {
          const idx = relatedWhitelist
            .map((c) => related.columns.indexOf(c))
            .filter((i) => i >= 0);
          return {
            title: related.title,
            columns: idx.map((i) => related.columns[i]),
            rows: related.rows.map((row) => idx.map((i) => row[i])),
          };
        })()
      : related;

  return (
    <Stack gap="md">
      <DataBrowserToolbar
        moduleValue={module}
        moduleOptions={moduleOptions}
        onChangeModule={onChangeModule}
        tableValue={table}
        tableOptions={tableOptions}
        onChangeTable={onChangeTable}
        searchText={search}
        onSearchChange={onSearchChange}
        totalLabel={`총 ${total.toLocaleString()}건`}
        rangeLabel={rangeLabel}
        onPrev={onPrev}
        onNext={onNext}
      />

      {/* 표는 오른쪽까지 꽉 채워 전폭으로 렌더 — 행 상세는 드로어로 분리. */}
      {loading && rows.length === 0 ? (
        <div css={gridMsgStyle}>
          <Spinner />
          <span>데이터 로딩 중…</span>
        </div>
      ) : error ? (
        <div css={gridMsgStyle}>
          <span>❌ {error}</span>
          <Button variant="outline" tone="neutral" onClick={onRetry}>
            재시도
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div css={gridMsgStyle}>조건에 맞는 행이 없습니다.</div>
      ) : (
        <DataGrid
          columns={gridColumns}
          rows={gridRows}
          selectedIndex={selectedIndex}
          onSelectRow={onSelectRow}
          comments={getColumnComments(module, table)}
        />
      )}

      {/* 행 클릭 시 오른쪽에서 슬라이드로 덮어 씌우는 상세 드로어. */}
      <Drawer open={detailOpen} onClose={onCloseDetail} label="행 상세">
        <div css={drawerHeaderStyle}>
          <Button variant="text" tone="neutral" onClick={onCloseDetail}>
            닫기 ✕
          </Button>
        </div>
        <div css={drawerBodyStyle}>
          <RowDetailPanel
            subtitle={selectedRow ? `${table} · ${columns[0]}=${selectedRow[0]}…` : undefined}
            entries={selectedRow ? detailIdx.map((j) => ({ key: columns[j], value: linkifyValue(selectedRow[j]) })) : []}
            fkLabel={fk ? `${fk.label} 보기 →` : undefined}
            onOpenFk={onOpenFk}
            related={relatedView}
            relatedLoading={relatedLoading}
            relatedError={relatedError}
            onOpenDoc={docRow ? () => setDocOpen(true) : undefined}
          />
        </div>
      </Drawer>

      {/* 문서 뷰어 — md/이미지를 인라인 렌더. 행 상세보다 넓게 둔다(문서 가독성). */}
      <Drawer open={docOpen} onClose={() => setDocOpen(false)} label="문서 보기" width={720}>
        <div css={drawerHeaderStyle}>
          <Button variant="text" tone="neutral" onClick={() => setDocOpen(false)}>
            닫기 ✕
          </Button>
        </div>
        <div css={drawerBodyStyle}>
          {docRow && (
            <DocViewer
              // 수집 모듈명(md_path 첫 세그먼트) — 브라우저의 module(=DB 스키마)과 다르다.
              module={docRow.module ?? module}
              title={docRow.title}
              publisher={docRow.publisher}
              docDate={docRow.docDate}
              sourceUrl={docRow.sourceUrl}
              mdPath={docRow.mdPath}
              imagePaths={docRow.imagePaths}
              open={docOpen}
            />
          )}
        </div>
      </Drawer>

      {/*<p css={noteStyle}>*/}
      {/*  모듈 전용 필드는 하드코딩하지 않음 — 컬럼은 대상 테이블 응답 스키마에서 그대로 렌더링.*/}
      {/*</p>*/}
    </Stack>
  );
}
